import { X, MapPin, TrendingUp, Lightbulb } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BusinessCard } from "@/components/tradesman/business-card";

interface EmptyStateProps {
  hasFilters: boolean;
  cityFilter: string | null;
  onClearFilters: () => void;
  featuredAds: TradesmanAd[];
}

interface TradesmanAd {
  id: string;
  businessName: string;
  logo?: string | null;
  phone: string;
  email: string;
  tagline?: string | null;
  servicesDescription?: string | null;
  serviceArea?: string | null;
  website?: string | null;
  city?: string | null;
  state?: string | null;
  planType: string;
  photoGallery?: string[];
  category: {
    displayName: string;
    icon?: string | null;
  };
}

export function EmptyState({ hasFilters, cityFilter, onClearFilters, featuredAds }: EmptyStateProps) {
  // Get unique cities from featured ads
  const uniqueCities = Array.from(
    new Set(featuredAds.map(ad => ad.city).filter(Boolean))
  ).slice(0, 4);

  return (
    <div className="space-y-8">
      {/* Main Empty State Message */}
      <div className="text-center py-16 bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
        <div className="mb-6">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
            {hasFilters ? (
              <X className="w-10 h-10 text-emerald-600 dark:text-emerald-400" />
            ) : (
              <MapPin className="w-10 h-10 text-emerald-600 dark:text-emerald-400" />
            )}
          </div>
          <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
            {hasFilters ? "No matches found" : "No professionals in your area yet"}
          </h3>
          <p className="text-slate-600 dark:text-slate-400 max-w-md mx-auto">
            {hasFilters
              ? "Try adjusting your filters or search to find more professionals."
              : "We're growing our network. Check back soon or browse featured professionals nationwide."}
          </p>
        </div>

        {hasFilters && (
          <Button
            onClick={onClearFilters}
            variant="outline"
            className="gap-2"
          >
            <X className="w-4 h-4" />
            Clear all filters
          </Button>
        )}
      </div>

      {/* Suggestions Section */}
      {featuredAds.length > 0 && (
        <div className="bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-950/30 dark:to-slate-900 rounded-xl p-6 border border-emerald-200 dark:border-emerald-800">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              Featured Professionals
            </h3>
          </div>
          <p className="text-slate-600 dark:text-slate-400 mb-6">
            Explore top-rated service providers across the country
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredAds.map(ad => (
              <BusinessCard key={ad.id} ad={ad} />
            ))}
          </div>
        </div>
      )}

      {/* Nearby Cities Suggestions */}
      {!hasFilters && uniqueCities.length > 0 && (
        <div className="bg-white dark:bg-slate-900 rounded-xl p-6 border border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-2 mb-4">
            <Lightbulb className="w-5 h-5 text-amber-500" />
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              Try searching nearby cities
            </h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {uniqueCities.map(city => (
              <Button
                key={city}
                variant="outline"
                size="sm"
                onClick={() => {
                  const cityInput = document.querySelector(
                    'input[placeholder="Search by business name or service..."]'
                  ) as HTMLInputElement;
                  if (cityInput) {
                    cityInput.value = city || "";
                    cityInput.dispatchEvent(new Event('input', { bubbles: true }));
                  }
                }}
                className="gap-1"
              >
                <MapPin className="w-3 h-3" />
                {city}
              </Button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}